/**
 * Timezone utility functions for handling local time vs UTC
 */

/**
 * Get the current local date in YYYY-MM-DD format
 * This ensures users see their local date, not UTC date
 */
export const getLocalDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * The board's current WORKING date (YYYY-MM-DD): the date the user last selected on the board — but ONLY if it
 * was chosen on the SAME real-world day (otherwise "today"). Every board-linked view (Board, Simulate tab, CRNA
 * and Anesthesiologists tabs) must read THIS so they all operate on — and clear per-day overlays for — the SAME
 * date. Without it, the Simulate tab defaulted to today while the board could be parked on another date, so a
 * new sim generated/cleared the wrong day and stale overlays (e.g. float CRNAs) lingered on the board.
 */
export const getBoardWorkingDate = (): string => {
  const today = getLocalDateString();
  try {
    const saved = localStorage.getItem('oc-board-selected-date');
    const savedOn = localStorage.getItem('oc-board-selected-saved-on');
    // Keep the chosen working date only across refreshes on the SAME real-world day.
    if (saved && /^\d{4}-\d{2}-\d{2}$/.test(saved) && savedOn === today) return saved;
  } catch { /* localStorage unavailable */ }
  return today;
};

/**
 * Convert a local date string (YYYY-MM-DD) to UTC start of day
 * @param localDateString - Date in YYYY-MM-DD format (local time)
 * @returns UTC datetime string for start of day
 */
export const localDateToUtcStart = (localDateString: string): string => {
  // Create a date object in local timezone for the given date
  const localDate = new Date(localDateString + 'T00:00:00');
  // Convert to UTC
  return localDate.toISOString();
};

/**
 * Convert a local date string (YYYY-MM-DD) to UTC end of day
 * @param localDateString - Date in YYYY-MM-DD format (local time)
 * @returns UTC datetime string for end of day
 */
export const localDateToUtcEnd = (localDateString: string): string => {
  // Create a date object in local timezone for the given date
  const localDate = new Date(localDateString + 'T23:59:59');
  // Convert to UTC
  return localDate.toISOString();
};

/**
 * Convert a UTC datetime string to local time for display
 * @param utcDateTime - UTC datetime string
 * @returns Local datetime string for display
 */
export const utcToLocalDisplay = (utcDateTime: string): string => {
  const date = new Date(utcDateTime);
  return date.toLocaleString();
};

/**
 * Convert a UTC datetime string to local time string (HH:MM format)
 * @param utcDateTime - UTC datetime string
 * @returns Local time string in HH:MM format
 */
export const utcToLocalTime = (utcDateTime: string): string => {
  const date = new Date(utcDateTime);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit',
    minute: '2-digit',
    hour12: false 
  });
};

/**
 * Convert a UTC datetime string to local date string (YYYY-MM-DD format)
 * @param utcDateTime - UTC datetime string
 * @returns Local date string in YYYY-MM-DD format
 */
export const utcToLocalDate = (utcDateTime: string): string => {
  const date = new Date(utcDateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get the user's timezone offset in minutes
 * @returns Timezone offset in minutes
 */
export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset();
};

/**
 * Format a browser timezone offset (minutes, as returned by Date.getTimezoneOffset())
 * into the backend's "UTC-6" / "UTC+5:30" string form.
 *
 * NOTE on sign: getTimezoneOffset() returns the minutes you ADD to local time to get
 * UTC, so a zone BEHIND UTC (e.g. US Central) yields a POSITIVE value (+300 for CDT)
 * which must be formatted as "UTC-5". Hence the inverted sign below.
 */
export const formatOffsetMinutesAsUtcString = (offsetMinutes: number): string => {
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes <= 0 ? '+' : '-';
  return `UTC${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}`;
};

/**
 * Get the DST-correct timezone offset string ("UTC-5"/"UTC-6"/...) for a SPECIFIC
 * local date (YYYY-MM-DD), using the browser's own timezone rules.
 *
 * Keyed off the given date — resolved at local NOON to dodge the non-existent
 * 2:00–3:00am local hour on spring-forward day — NOT off "today". So allocating an
 * April (CDT = UTC-5) board from a January (CST = UTC-6) session still yields the
 * April offset. This is the single source of truth the board uses so that creation,
 * display, simulation, and allocation all agree on which wall-clock time a UTC
 * instant maps to. Mismatching them is exactly what made early-morning cases appear
 * to be staffed by zero-lead nurses (the allocator ran on a fixed UTC-6 while the
 * board displayed DST-aware).
 *
 * @param localDateString - Date in YYYY-MM-DD format (local time)
 * @returns Timezone string for the backend, e.g. "UTC-5" or "UTC-6"
 */
export const getTimezoneOffsetStringForDate = (localDateString: string): string => {
  const dateAtLocalNoon = new Date(`${localDateString}T12:00:00`);
  return formatOffsetMinutesAsUtcString(dateAtLocalNoon.getTimezoneOffset());
};

/**
 * Get the DST-correct offset string ("UTC-5" in CDT / "UTC-6" in CST) for US Central
 * time (America/Chicago) on a specific date, INDEPENDENT of the machine's own
 * timezone. Used to pin simulation to Central so generated shift + case times are
 * unambiguous no matter what timezone the computer running the app is set to.
 *
 * @param localDateString - Date in YYYY-MM-DD format
 * @returns Timezone string for the backend, e.g. "UTC-5" or "UTC-6"
 */
export const getCentralOffsetStringForDate = (localDateString: string): string => {
  // A fixed instant on that date (noon UTC dodges the DST-transition edge hours).
  const instant = new Date(`${localDateString}T12:00:00Z`);
  // Ask Intl for Central's UTC offset on that instant, e.g. "GMT-05:00" / "GMT-06:00".
  const tzName = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    timeZoneName: 'longOffset',
  })
    .formatToParts(instant)
    .find((p) => p.type === 'timeZoneName')?.value ?? 'GMT-06:00';
  const m = tzName.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!m) return 'UTC-6';
  const minutes = m[3] ? parseInt(m[3], 10) : 0;
  return `UTC${m[1]}${parseInt(m[2], 10)}${minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

/**
 * Get the user's timezone name
 * @returns Timezone name (e.g., "America/Denver")
 */
export const getTimezoneName = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Convert a local datetime string to UTC
 * @param localDateTime - Local datetime string (YYYY-MM-DDTHH:MM format)
 * @returns UTC datetime string
 */
export const localDateTimeToUtc = (localDateTime: string): string => {
  // Create a date object in local timezone
  const localDate = new Date(localDateTime);
  // Convert to UTC
  return localDate.toISOString();
};
